<template>
    <form-fields>
        <toolbar-buttons
            :no-scroll="true"
        >
            <multiselect
                class="cz3__text-font"
                v-model="selectedFont"
                :searchable="false"
                :show-labels="false"
                :options="availableFonts"
                :allowEmpty="false"
            >
                <template slot="singleLabel" slot-scope="props">
                    <span :style="getFontStyle(props.option)">{{props.option.name}}</span>
                </template>
                <template slot="option" slot-scope="props">
                    <span :style="getFontStyle(props.option)">{{props.option.name}}</span>
                </template>
            </multiselect>
        </toolbar-buttons>

        <step-buttons
            :class="{
                'cz3__hide-lg': buttonsSm,
            }"
        >
            <secondary-button
                class="cz3__flex-1"
                :label="$t('V3_BACK')"
                @click="back"
            />

            <action-button
                class="cz3__flex-1"
                :label="$t('V3_NEXT')"
                @click="next"
            />
        </step-buttons>
    </form-fields>
</template>

<style lang="scss">
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .multiselect.cz3__text-font {
            background: #f5f5f5;
            border-radius: 8px;
            border: none;

            &:focus {
                outline: 2px solid #000;
                outline-offset: 2px;
            }

            .multiselect__tags {
                position: relative;

                min-height: 68px;
                height: 68px;

                display: flex;
                align-items: center;
                padding: 0 0px 0 18px;
                gap: 18px;

                background: #f5f5f5;
                border-radius: 8px;
                border: none;

                &:before {
                    flex: 0 0 auto;

                    content: '';

                    width: 20px;
                    height: 20px;

                    background: transparent url(../../assets/v2/text-fonts.svg) no-repeat center/contain;
                }
            }

            .multiselect__select {
                height: 68px;
                z-index: 1;
            }

            .multiselect__single {
                font-family: $font-main;
                font-weight: 500;
                font-size: 16px;

                border-radius: 0;

                padding: 0;
                padding-left: 5px;
                margin-bottom: 0;

                vertical-align: middle;

                min-height: 1px;
                height: auto;
                line-height: 1;

                background: transparent;
            }

            .multiselect__input {
                font-family: $font-main;
                font-weight: 500;
                font-size: 16px;
                line-height: 28px;

                margin-bottom: 0;
                background: transparent;
            }

            .multiselect__option.multiselect__option--highlight {
                background: #f1f1f1;
                color: #000;
            }

            .multiselect__content-wrapper {
                border-radius: 8px;

                background: #fff;
                border: 1px solid #ccc;

                z-index: 100000;
            }

            &.multiselect--above {
                .multiselect__content-wrapper {
                    margin-top: -5px;
                }
            }
        }
    }
</style>

<script>
    import ToolbarButtons from './ToolbarButtons.vue';
    import FormFields from './FormFields.vue';
    import ActionButton from './ActionButton.vue';
    import SecondaryButton from './SecondaryButton.vue';
    import StepButtons from './StepButtons.vue';

    import * as constants from '../../constants';

    export default {
        components: {
            ToolbarButtons,
            FormFields,
            ActionButton,
            SecondaryButton,
            StepButtons,
        },

        props: {
            buttonsSm: Boolean,
        },

        data() {
            return {
                selectedFont: '',
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            editPlacement() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    return this.editPlacements[0];
                }

                return null;
            },

            editComponent() {
                if (this.editPlacement) {
                    return this.viewController.selectedAtPlacement(this.editPlacement.code);
                }

                return null;
            },

            filledTextPlacements() {
                return this.$store.getters.filledTextPlacements;
            },

            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            nextStep() {
                return this.$store.getters.nextStepV3;
            },

            /**
             * Figure out a list of available fonts.
             */
            availableFonts() {
                if (this.editPlacement) {
                    const fonts = [];

                    this.editPlacement.components.forEach((c) => {
                        const font = c.description.find((d) => d.definition === constants.DEFINITION_FONT);

                        if (font) {
                            if (!fonts.some((f) => f.code === font.code)) {
                                const copy = { ...font };

                                if (copy.custom == null) {
                                    copy.custom = {};
                                }

                                copy.custom = { ...copy.custom, ...c.custom };

                                fonts.push(copy);
                            }
                        }
                    });

                    return fonts;
                }

                return [];
            },

            /**
             * Secondary bottles array.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },
        },

        watch: {
            selectedFont() {
                this.updateFont();
            },

            /**
             * Load font files so we could show the fonts preview.
             */
            availableFonts() {
                this.$store.dispatch('loadFonts', this.availableFonts);
            },
        },

        mounted() {
            this.loadFromPlacement();

            if (this.availableFonts?.length > 0) {
                this.$store.dispatch('loadFonts', this.availableFonts);
            }
        },

        methods: {
            back() {
                if (this.personalizationMethod === 'printing') {
                    this.$store.dispatch('setSelectedSubStepV3', 'text-color');
                } else {
                    this.$store.dispatch('setSelectedSubStepV3', 'text');
                }
            },

            /**
             * Update personalization.
             */
            updateFont() {
                this.viewController.updateSimilarComponent(this.editPlacement.code, constants.DEFINITION_FONT, this.selectedFont.code);
            },

            next() {
                this.$store.dispatch('setSelectedSubStepV3', 'text-alignment');
            },

            /**
             * Enters text editing mode.
             */
            beginEdit(placement) {
                this.$store.dispatch('lockSwipe', true);
                this.$store.dispatch('editPlacement', placement);

                this.loadFromPlacement();
            },

            loadFromPlacement() {
                const font = this.editComponent.description.find((d) => d.definition === constants.DEFINITION_FONT);
                if (font) {
                    this.selectedFont = { ...font, ...{ custom: { ...font.custom, ...this.editComponent.custom } } };
                }

                if (this.editComponent.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', this.editComponent.custom['v-design-on']);
                }
            },

            /**
             * Build font style object for the font selector.
             */
            getFontStyle(option) {
                const css = {};

                if (option.custom && option.custom['v-webfont-family']) {
                    css.fontFamily = option.custom['v-webfont-family'];
                }

                return css;
            },
        },
    };
</script>
